import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL = {
  confirmed: { text: "Confirmada", cls: "bg-secondary text-secondary-foreground" },
  completed: { text: "Completada", cls: "bg-slate-200 text-slate-700" },
  cancelled: { text: "Cancelada", cls: "bg-red-50 text-red-700" },
};

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    api.get("/appointments/mine").then((r) => setAppts(r.data)).catch(() => {});
  }, []);

  const upcoming = appts.filter((a) => a.status === "confirmed");
  const past = appts.filter((a) => a.status !== "confirmed");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 glass-nav sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo-link">
            <BrandLogo />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">Hola, {user?.name}</span>
            <Button variant="outline" onClick={() => logout().then(() => navigate("/"))} data-testid="dashboard-logout-btn">Cerrar sesión</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="overline">Mi cuenta</p>
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-3">Tus citas</h1>
          </div>
          <Button onClick={() => navigate("/book")} data-testid="dashboard-new-appt-btn">Agendar nueva cita</Button>
        </div>

        <section className="mb-12">
          <h2 className="font-display text-xl mb-4">Próximas</h2>
          {upcoming.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="p-8 text-slate-500" data-testid="dashboard-no-upcoming">Aún no tienes citas próximas.</CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {upcoming.map((a) => (
                <Card key={a.id} className="border-slate-200 card-hover" data-testid={`upcoming-card-${a.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg">{a.service_name}</p>
                        <p className="text-sm text-slate-600 mt-1">{a.date} · {a.time}</p>
                        <p className="text-sm text-slate-500 mt-3">${a.service_price.toFixed(2)} · {a.service_duration_min} min</p>
                      </div>
                      <Badge className={STATUS_LABEL[a.status].cls}>{STATUS_LABEL[a.status].text}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-display text-xl mb-4">Historial</h2>
          {past.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="p-8 text-slate-500" data-testid="dashboard-no-history">Sin historial todavía.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {past.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-5 py-4" data-testid={`past-row-${a.id}`}>
                  <div>
                    <p className="font-display">{a.service_name}</p>
                    <p className="text-xs text-slate-500">{a.date} · {a.time}</p>
                  </div>
                  <Badge className={STATUS_LABEL[a.status].cls}>{STATUS_LABEL[a.status].text}</Badge>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
